import { type RootState } from 'app/store';

export type ProgressToastKind = 'upload' | 'move' | 'copy' | 'delete';
export type ProgressToastStatus = 'pending' | 'done' | 'failed';

export type ProgressToastDestination = {
	label: string;
	folderId: string;
};

export type ProgressToast = {
	id: string;
	kind: ProgressToastKind;
	status: ProgressToastStatus;
	count: number;
	itemName?: string;
	destination?: ProgressToastDestination;
	progress: number | null;
	startedAt: number;
};

type ProgressToastsState = {
	toasts: ProgressToast[];
};

const initialState: ProgressToastsState = { toasts: [] };

type StartAction = {
	type: 'PROGRESS_TOAST_START';
	payload: Omit<ProgressToast, 'status' | 'startedAt'>;
};
type UpdateAction = {
	type: 'PROGRESS_TOAST_UPDATE';
	payload: { id: string; progress: number | null };
};
type CompleteAction = {
	type: 'PROGRESS_TOAST_COMPLETE';
	payload: { id: string; count?: number; itemName?: string };
};
type FailAction = { type: 'PROGRESS_TOAST_FAIL'; payload: { id: string } };
type DismissAction = { type: 'PROGRESS_TOAST_DISMISS'; payload: { id: string } };
type LogoutAction = { type: 'LOGGED_OUT_USER' };
type PurgeAction = { type: 'PURGE_STATE' };

type ProgressToastsAction =
	| StartAction
	| UpdateAction
	| CompleteAction
	| FailAction
	| DismissAction
	| LogoutAction
	| PurgeAction;

const progressToastsReducer = (state = initialState, action: ProgressToastsAction): ProgressToastsState => {
	switch (action.type) {
		case 'PROGRESS_TOAST_START': {
			const without = state.toasts.filter((t) => t.id !== action.payload.id);
			return {
				toasts: [
					...without,
					{
						...action.payload,
						status: 'pending',
						startedAt: Date.now(),
					},
				],
			};
		}
		case 'PROGRESS_TOAST_UPDATE': {
			return {
				toasts: state.toasts.map((t) =>
					t.id === action.payload.id ? { ...t, progress: action.payload.progress } : t,
				),
			};
		}
		case 'PROGRESS_TOAST_COMPLETE': {
			return {
				toasts: state.toasts.map((t) =>
					t.id === action.payload.id
						? {
								...t,
								status: 'done',
								progress: 1,
								...(action.payload.count !== undefined && { count: action.payload.count }),
								...(action.payload.itemName !== undefined && { itemName: action.payload.itemName }),
							}
						: t,
				),
			};
		}
		case 'PROGRESS_TOAST_FAIL': {
			return {
				toasts: state.toasts.map((t) => (t.id === action.payload.id ? { ...t, status: 'failed' } : t)),
			};
		}
		case 'PROGRESS_TOAST_DISMISS': {
			return { toasts: state.toasts.filter((t) => t.id !== action.payload.id) };
		}
		case 'LOGGED_OUT_USER':
		case 'PURGE_STATE':
			return initialState;
		default:
			return state;
	}
};

export default progressToastsReducer;

export const getProgressToasts = (state: RootState): ProgressToast[] => state.progressToasts.toasts;
